/**
 * Domain hook for GitHub Codespace management.
 *
 * Uses ConnectRPC for all operations.
 *
 * @module
 */

import { useState, useCallback, useMemo } from "react";
import type { Codespace, UseCodespacesResult } from "@grackle-ai/web-components";
import { extractErrorMessage } from "./grackleError.js";
import type { DomainHook } from "./domainHook.js";
import { coreClient as grackleClient } from "./useGrackleClient.js";
import { protoToCodespace } from "./proto-converters.js";

export type { UseCodespacesResult } from "@grackle-ai/web-components";

/**
 * Hook that manages codespace state and actions via ConnectRPC.
 *
 * @returns Codespace state and actions.
 */
export function useCodespaces(): UseCodespacesResult {
  const [codespaces, setCodespaces] = useState<Codespace[]>([]);
  const [codespaceError, setCodespaceError] = useState("");
  const [codespaceListError, setCodespaceListError] = useState("");
  const [codespaceCreating, setCodespaceCreating] = useState(false);

  const listCodespaces = useCallback(async (githubAccountId?: string) => {
    try {
      const resp = await grackleClient.listCodespaces({ githubAccountId: githubAccountId ?? "" });
      setCodespaces(resp.codespaces.map(protoToCodespace));
      setCodespaceListError(resp.error);
    } catch {
      // empty
    }
  }, []);

  const createCodespace = useCallback(
    async (repo: string, machine?: string) => {
      setCodespaceCreating(true);
      setCodespaceError("");
      try {
        await grackleClient.createCodespace({ repo, machine: machine ?? "" });
        setCodespaceCreating(false);
        await listCodespaces();
      } catch (err) {
        setCodespaceCreating(false);
        const message = extractErrorMessage(err, "Failed to create codespace");
        setCodespaceError(message);
      }
    },
    [listCodespaces],
  );

  const domainHook: DomainHook = useMemo(
    () => ({
      onConnect: () => listCodespaces(),
      onDisconnect: () => {},
      handleEvent: () => false,
    }),
    [listCodespaces],
  );

  return {
    codespaces,
    codespaceError,
    codespaceListError,
    codespaceCreating,
    listCodespaces,
    createCodespace,
    domainHook,
  };
}
